/**
 * In-house mini-markdown parser. Zero dependencies — kept intentionally
 * small as an alternative to pulling in react-markdown (~30KB gzipped).
 *
 * Supports:
 *   Block-level:
 *     # Heading        → h1 (Henaket spec: 40/700/lh 64)
 *     ## Heading       → h2 (30/600/40)
 *     ### Heading      → h3 (24/600/32)
 *     #### Heading     → h4 (20/600/32)
 *     ##### Heading    → h5 (16/600/24)
 *     ###### Heading   → h6 (13/500/20)
 *     - item           → <ul><li>
 *     * item           → <ul><li>
 *     1. item          → <ol><li>
 *     plain paragraph  → <p>
 *     blank line       → block separator
 *     | a | b |        → <table> (GFM, 0.36.111 — requires separator row)
 *     | - | - |
 *     | x | y |
 *
 *   Inline (within paragraphs / headings / list items / table cells):
 *     **bold**         → <strong>
 *     *italic*         → <em>
 *     _italic_         → <em>
 *     `code`           → <code>
 *     [text](url)      → <a>  (smart-routed: /-prefixed → React Router same-tab,
 *                              else target="_blank" new tab)
 *     [text](url){: target="_blank" hyperlink="ext"}
 *                      → <a> with attr-list overrides. 0.38.45:
 *                        `target="_blank"` forces new-tab (even on
 *                        /-paths); `hyperlink="ext"` additionally
 *                        appends the Henaket open_in_new icon
 *                        after the link text. Any other attrs in
 *                        the block are ignored (fail-closed).
 *
 * Does NOT support:
 *   nested lists, blockquotes, fenced code blocks, images, raw HTML,
 *   autolinks, footnotes, column-alignment in tables. Documented for
 *   posterity — if you reach for one of these, that's the trigger to
 *   evaluate react-markdown.
 *
 * Architectural notes:
 *   - parseBlocks() splits input on blank lines and routes each block
 *     to a renderer. Headings and lists must occupy their own block
 *     (separated by blank line) — typical markdown convention.
 *   - parseInline() walks the text and tokenizes inline marks. Order
 *     of pattern checks matters: links before code, code before bold
 *     (which is greedy and would otherwise eat ` characters).
 *   - <code>, <strong>, <em>, <a> are returned as React elements with
 *     stable keys so React reconciliation is happy on re-render.
 *
 * Test coverage: visual via the seeded About page. If we add unit
 * tests, they'd live next to this file as markdown.test.js.
 */
import { Link } from 'react-router-dom';
import BuiltinIcon from '../components/BuiltinIcon';

/* 0.45.23 - The link syntax, as the admin hints state it. One text for
 * every Markdown field (Markdown element, FAQ answers, collection Rich
 * text, legacy page bodies), so they never disagree. */
export const LINK_HINT = 'Links: [text](/address) opens on this site, [text](https://...) in a new tab. For an external link with the external-link icon, add {: hyperlink="ext"} right after it, no space: [text](https://...){: hyperlink="ext"}.';


/**
 * 0.38.45 - Parse a Kramdown / Pandoc IAL (Inline Attribute List)
 * block right after a link's closing paren:
 *
 *     [text](url){: target="_blank" hyperlink="ext"}
 *
 * Returns a shallow attrs dict, or empty object when the block is
 * absent or malformed. Only handles the specific attrs we care
 * about here (target, hyperlink) - not a general-purpose IAL
 * parser. Whitespace between attrs is any run of spaces/tabs;
 * values must be double-quoted with no embedded quotes; missing
 * quotes -> the whole IAL is ignored (fail closed rather than
 * silently mis-parsing).
 */
function _parseLinkAttrs(rawIal) {
  const out = {};
  if (typeof rawIal !== 'string' || rawIal.length === 0) return out;
  // 0.38.49 - Normalize smart quotes (U+201C, U+201D, U+2018, U+2019)
  // to straight ASCII quotes before parsing. Some admin input paths
  // (rich-text editors, copy-paste from Word or Google Docs, some
  // IME configurations) auto-transform " into curly variants, which
  // silently broke the regex below - the IAL was still stripped
  // from the visible text but no attrs matched, so hyperlink="ext"
  // stopped adding the external-link icon. Normalizing here means
  // admin can copy-paste from anywhere without knowing which quote
  // variant their source used.
  const normalized = rawIal
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'");
  // Strip the wrapping `{:` ... `}` characters, then split on runs
  // of whitespace. Each token is `key="value"`.
  const inner = normalized.replace(/^\{:\s*/, '').replace(/\s*\}$/, '');
  if (inner.length === 0) return out;
  const tokenRe = /([a-zA-Z_-]+)="([^"]*)"/g;
  let m;
  while ((m = tokenRe.exec(inner)) !== null) {
    out[m[1]] = m[2];
  }
  return out;
}


// ============================================================
// Inline pass — handles bold, italic, code, links inside a text run
// ============================================================

/**
 * Single inline match. Each pattern returns its match info or null.
 *
 * The order patterns are TRIED matters: we look for the EARLIEST
 * match across all patterns, but if two start at the same position,
 * the one declared first wins. That's why links and code come before
 * bold/italic — they have unambiguous boundary characters and should
 * "win" if their delimiters happen to overlap a `**` pattern.
 */
const INLINE_PATTERNS = [
  {
    name: 'link',
    // [text](url){: target="_blank" hyperlink="ext"}
    // 0.38.45 - Third capture group optional: matches an IAL block
    // (`{: key="value" ... }`) directly after the closing paren.
    // Whitespace between paren and IAL is NOT permitted (kramdown
    // convention). The IAL is opaque to the regex - _parseLinkAttrs
    // does the split into attributes below. When absent, groups[2]
    // is undefined and the render falls through to the pre-0.38.45
    // behavior (external URL -> new tab, /-prefix -> same tab).
    regex: /\[([^\]\n]+)\]\(([^)\s\n]+)\)(\{:[^}]*\})?/,
    render: (groups, key) => {
      const [text, url, rawIal] = groups;
      const attrs = _parseLinkAttrs(rawIal);
      // Smart routing: /-prefixed paths use React Router for same-tab
      // SPA navigation; everything else (https://..., mailto:, tel:)
      // opens in a new tab with security attributes.
      // 0.38.45 - `hyperlink="ext"` in the IAL forces the external
      // treatment (new tab + rel + open-in-new icon) even on /-paths;
      // any other value keeps the smart routing. `target="_blank"`
      // in the IAL adds new-tab behavior to /-paths that would
      // otherwise open in the same tab, without adding the icon.
      const forceExternal = attrs.hyperlink === 'ext';
      const forceNewTab = attrs.target === '_blank' || forceExternal;
      const isInternalPath = url.startsWith('/');
      if (isInternalPath && !forceNewTab && !forceExternal) {
        return <Link key={key} to={url}>{text}</Link>;
      }
      // External or forced-new-tab: render as <a>. Append the
      // Henaket open_in_new icon when hyperlink="ext" (per admin
      // spec - the icon is the visual affordance for "leaves this
      // site"). Icon sits after the text with a small gap; the
      // <a> stays inline so line-wrapping keeps the icon glued to
      // the last word.
      return (
        <a key={key} href={url}
           target={forceNewTab || !isInternalPath ? '_blank' : undefined}
           rel={forceNewTab || !isInternalPath ? 'noopener noreferrer' : undefined}
           className="linkText">
          {text}
          {forceExternal && (
            <>
              {'\u00A0'}
              <BuiltinIcon name="open_in_new" size="1em"
                           title="Opens in a new tab" />
            </>
          )}
        </a>
      );
    },
  },
  {
    name: 'code',
    regex: /`([^`\n]+)`/,
    render: (groups, key) => <code key={key}>{groups[0]}</code>,
  },
  {
    name: 'bold',
    regex: /\*\*([^*\n]+)\*\*/,
    render: (groups, key) => <strong key={key}>{groups[0]}</strong>,
  },
  {
    name: 'italic-star',
    // Single * not preceded or followed by another * (to avoid eating
    // half of a **bold** marker)
    regex: /(?<!\*)\*([^*\n]+)\*(?!\*)/,
    render: (groups, key) => <em key={key}>{groups[0]}</em>,
  },
  {
    name: 'italic-underscore',
    // 0.39.87 - CommonMark-style: underscore emphasis only at WORD
    // BOUNDARIES. The opening `_` must not be preceded by a word char
    // and the closing `_` must not be followed by one - so intraword
    // underscores in identifiers / keys (dropdown_nice_classes,
    // field_1) stay LITERAL instead of italicizing the middle
    // segment ("_nice_" -> nice). Real emphasis (`word _italic_ word`)
    // still works. Asterisk italic (`*x*`) is unaffected.
    regex: /(?<![A-Za-z0-9])_([^_\n]+)_(?![A-Za-z0-9])/,
    render: (groups, key) => <em key={key}>{groups[0]}</em>,
  },
];


function parseInline(text, keyPrefix = '') {
  // Returns an array of React nodes (mix of strings and elements).
  const tokens = [];
  let remaining = text;
  let counter = 0;

  while (remaining.length > 0) {
    // Find the earliest match across all patterns
    let earliest = null;
    for (const pattern of INLINE_PATTERNS) {
      const m = pattern.regex.exec(remaining);
      if (m && (!earliest || m.index < earliest.match.index)) {
        earliest = { pattern, match: m };
      }
    }

    if (!earliest) {
      // No more inline marks — push the rest as plain text
      tokens.push(remaining);
      break;
    }

    // Text before the match
    if (earliest.match.index > 0) {
      tokens.push(remaining.substring(0, earliest.match.index));
    }

    // The match itself rendered as element
    const key = `${keyPrefix}-${counter++}`;
    tokens.push(earliest.pattern.render(earliest.match.slice(1), key));

    // Continue past the match
    remaining = remaining.substring(earliest.match.index + earliest.match[0].length);
  }

  return tokens;
}


// ============================================================
// Block pass — heading / list / paragraph dispatch
// ============================================================

const HEADING_REGEX = /^(#{1,6})\s+(.+)$/;
const UNORDERED_REGEX = /^[-*]\s+(.+)$/;
const ORDERED_REGEX = /^\d+\.\s+(.+)$/;
// 0.36.111 — Table syntax: a line of the form `| col | col | col |`.
// Tables must be at least two rows long; the second row is a separator
// of `|` and `-` chars (GFM convention). We detect a table by seeing
// (a) a row line, (b) a separator row immediately below. Without a
// valid separator, the lines fall through to paragraph rendering as
// before, so non-table `|` content (like raw text "a | b") still
// works.
const TABLE_ROW_REGEX = /^\|(.+)\|$/;
const TABLE_SEPARATOR_REGEX = /^\|[\s\-:|]+\|$/;


/**
 * Render a single parsed block (already classified) into a React node.
 *
 * Paragraph rendering: each physical line in the source becomes its
 * own visual line in the output, separated by <br />. This is the
 * GitHub-flavored-markdown convention (and matches what typewriter-
 * habit users naturally expect from a textarea — press Enter once
 * for a new line, twice for a new paragraph). Diverges from strict
 * CommonMark, which joins single-newline lines with a space.
 *
 * Rationale: admins type addresses, contact details, and short
 * structured text in our pages. They'd expect:
 *
 *     ## Address
 *     40/55 Gyulbenkyan street
 *     0014, Yerevan, Armenia
 *
 * …to render with the street and city on separate lines, not joined.
 */
function renderBlock(block, index) {
  const key = `block-${index}`;
  switch (block.type) {
    case 'heading': {
      const Tag = `h${block.level}`;
      return <Tag key={key}>{parseInline(block.text, key)}</Tag>;
    }
    case 'paragraph': {
      // Interleave the inline-parsed lines with <br /> elements.
      // flatMap unwraps the per-line inline arrays into a single
      // flat children array.
      const children = block.lines.flatMap((line, i) => {
        const inlineNodes = parseInline(line, `${key}-l${i}`);
        // First line gets no leading <br/>; subsequent lines get one.
        return i === 0
          ? inlineNodes
          : [<br key={`${key}-br${i}`} />, ...inlineNodes];
      });
      return <p key={key}>{children}</p>;
    }
    case 'ul':
      return (
        <ul key={key}>
          {block.items.map((item, i) => (
            <li key={`${key}-li-${i}`}>{parseInline(item, `${key}-li-${i}`)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={key}>
          {block.items.map((item, i) => (
            <li key={`${key}-li-${i}`}>{parseInline(item, `${key}-li-${i}`)}</li>
          ))}
        </ol>
      );
    case 'table':
      // 0.36.111 — Simple GFM table. Header row in <thead>, body rows
      // in <tbody>. Each cell goes through parseInline so bold/italic/
      // code/links inside cells render correctly. The CSS in
      // MarkdownContent.module.css styles the table; no inline styles
      // here.
      return (
        <table key={key}>
          <thead>
            <tr>
              {block.header.map((cell, i) => (
                <th key={`${key}-h-${i}`}>{parseInline(cell, `${key}-h-${i}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={`${key}-r-${r}`}>
                {row.map((cell, c) => (
                  <td key={`${key}-r-${r}-c-${c}`}>{parseInline(cell, `${key}-r-${r}-c-${c}`)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    default:
      return null;
  }
}


// ============================================================
// Top-level entry — parses a full markdown source string
// ============================================================

/**
 * Parse a markdown source string into an array of React elements.
 *
 * Parsing model: LINE-ANCHORED. We walk the source line by line and
 * classify each line into one of:
 *   - blank line  → flush the current accumulating block
 *   - heading     → standalone block (single line); flushes any current
 *                  block first
 *   - list item   → joins with adjacent same-type list lines into a
 *                  list block; non-matching line types flush the list
 *   - plain text  → accumulates into a paragraph block until interrupted
 *                  by a blank line, heading, or list line
 *
 * IMPORTANT: this is line-anchored, NOT block-anchored. A heading line
 * is always recognized as a heading regardless of what follows on the
 * next line. Earlier versions of this parser required a heading to be
 * the only line in a blank-line-separated block — which forced admins
 * to type two blank lines after every `## Heading` for it to render
 * correctly. The new approach matches typical markdown behavior:
 *
 *   ## Heading
 *   Body text immediately below
 *
 * …now renders as `<h2>Heading</h2><p>Body text immediately below</p>`
 * with no blank line required between them. Same logic for lists: a
 * `- item` line immediately after a heading or paragraph starts a new
 * list without requiring a blank-line separator.
 *
 * Returns [] if input is empty/null/undefined.
 */
export function parseMarkdown(source) {
  if (!source || typeof source !== 'string') return [];
  const trimmed = source.trim();
  if (!trimmed) return [];

  // Normalize Windows-style line endings before line-splitting so
  // admins copy-pasting from Word, browsers, or Notepad get consistent
  // behavior with native Unix-newline content.
  const normalized = trimmed.replace(/\r\n/g, '\n');
  const lines = normalized.split('\n');

  const blocks = [];
  let current = null;

  // Helper: commit the in-progress block (paragraph or list) and reset.
  // Headings don't go through this — they're pushed directly because
  // they're single-line and never accumulate.
  const flush = () => {
    if (current) {
      blocks.push(current);
      current = null;
    }
  };

  // 0.36.111 — Use index-based iteration so the table branch can
  // look ahead at the separator row and consume subsequent body rows.
  for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
    const raw = lines[lineIdx];
    const trimmedLine = raw.trim();

    // Blank line ends whatever was being accumulated. Multiple blank
    // lines in a row are harmless — the second and later flushes are
    // no-ops since `current` is already null.
    if (trimmedLine === '') {
      flush();
      continue;
    }

    // 0.36.111 — Table detection. Two-line lookahead: this line is a
    // table row AND the next line is a separator row → start a table
    // block. Without that exact pattern, `|` content falls through
    // to the paragraph branch (so admins can still write text with
    // literal pipe characters in non-table contexts).
    if (TABLE_ROW_REGEX.test(trimmedLine)
        && lineIdx + 1 < lines.length
        && TABLE_SEPARATOR_REGEX.test(lines[lineIdx + 1].trim())) {
      flush();
      const header = splitTableRow(trimmedLine);
      const rows = [];
      let j = lineIdx + 2;  // skip header + separator
      while (j < lines.length) {
        const rowLine = lines[j].trim();
        if (rowLine === '' || !TABLE_ROW_REGEX.test(rowLine)) break;
        rows.push(splitTableRow(rowLine));
        j++;
      }
      blocks.push({ type: 'table', header, rows });
      lineIdx = j - 1;  // -1 because the for-loop will ++ next
      continue;
    }

    // Heading: always its own block. Push immediately after flushing
    // whatever was accumulating.
    const headingMatch = HEADING_REGEX.exec(trimmedLine);
    if (headingMatch) {
      flush();
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        text: headingMatch[2],
      });
      continue;
    }

    // Unordered list item: append to a current ul block, or start one.
    const ulMatch = UNORDERED_REGEX.exec(trimmedLine);
    if (ulMatch) {
      if (!current || current.type !== 'ul') {
        flush();
        current = { type: 'ul', items: [] };
      }
      current.items.push(ulMatch[1]);
      continue;
    }

    // Ordered list item: similar.
    const olMatch = ORDERED_REGEX.exec(trimmedLine);
    if (olMatch) {
      if (!current || current.type !== 'ol') {
        flush();
        current = { type: 'ol', items: [] };
      }
      current.items.push(olMatch[1]);
      continue;
    }

    // Plain text line: accumulate into a paragraph. Each physical
    // line becomes its own entry in `lines` — at render time these
    // are separated by <br /> elements (GFM-style line breaks; see
    // renderBlock for the rationale).
    if (!current || current.type !== 'paragraph') {
      flush();
      current = { type: 'paragraph', lines: [] };
    }
    current.lines.push(trimmedLine);
  }
  flush();

  return blocks.map((block, i) => renderBlock(block, i)).filter(Boolean);
}

// 0.36.111 — Split a `| a | b | c |` row into ['a', 'b', 'c'], trimmed.
// Trailing/leading empty cells from the outer pipes are dropped.
function splitTableRow(line) {
  return line.slice(1, -1).split('|').map(c => c.trim());
}


/**
 * Split a markdown body on H2 boundaries — used by the FAQ-type page
 * to extract Q/A pairs from a single body. Returns an array of
 * { question, answer } objects.
 *
 * If the body has content BEFORE the first H2 (an intro paragraph),
 * it's silently ignored. If there are no H2 headings at all, returns
 * an empty array and the page renderer falls back to article-mode.
 */
export function splitFaqBody(source) {
  if (!source || typeof source !== 'string') return [];

  const normalized = source.replace(/\r\n/g, '\n').trim();
  if (!normalized) return [];

  // Find all H2 positions. Match `## ` at line start (preceded by
  // newline or start-of-string).
  const h2Regex = /(?:^|\n)##\s+(.+)$/gm;
  const matches = [];
  let m;
  while ((m = h2Regex.exec(normalized)) !== null) {
    matches.push({
      question: m[1].trim(),
      startIndex: m.index,
      headerLength: m[0].length,
    });
  }

  if (matches.length === 0) return [];

  // For each match, the answer is everything between the end of its
  // header and the start of the next match (or end of string).
  return matches.map((match, i) => {
    const answerStart = match.startIndex + match.headerLength;
    const answerEnd = i + 1 < matches.length ? matches[i + 1].startIndex : normalized.length;
    const answer = normalized.substring(answerStart, answerEnd).trim();
    return {
      question: match.question,
      answer,
    };
  });
}
